import { formatUrl, InputConfig, isValidHttpUrl } from "@cpj-ai/panel-npm-package";
import {
  AuthAccountOrganizationBusinessTypeChoices,
  AuthAccountOrganizationSizeChoices,
  AuthAccountOrganizationTypeChoices,
  useGetCitiesLazyQuery,
  useGetIndustriesLazyQuery,
} from "@graphql/generated";
import { isValidPhoneNumber } from "libphonenumber-js";

export type ICompanyInformationFormData = {
  name: string;
  shortName?: string;
  nationalNumber?: string;
  industry?: {
    node: {
      id: string;
    } | null;
  };
  businessType?:
    | {
        value: AuthAccountOrganizationBusinessTypeChoices;
      }
    | AuthAccountOrganizationBusinessTypeChoices;
  type?:
    | {
        value: AuthAccountOrganizationTypeChoices;
      }
    | AuthAccountOrganizationTypeChoices;
  establishedAt?: Date;
  size?:
    | {
        value: AuthAccountOrganizationSizeChoices;
      }
    | AuthAccountOrganizationSizeChoices;
  about?: string;
  sectionTitle?: string;
  email: string;
  website?: string;
  city?:
    | {
        node: {
          id: string;
        } | null;
      }
    | string;
  contactNumber?: string;
  address?: string;
};

export const getInputConfigs = (formData: ICompanyInformationFormData): InputConfig[] => [
  {
    name: "sectionTitle",
    previewSize: 12,
    inputProps: {
      type: "title",
      label: "Company information:",
      className: "text-base font-bold",
    },
  },
  {
    name: "name",
    previewSize: {
      sm: 12,
      md: 4,
    },
    inputProps: {
      type: "text",
      label: "Company name:",
      placeholder: "Enter Company name",
    },
    rules: {
      required: "Company name is required",
      maxLength: {
        value: 20,
        message: "Company name is too long",
      },
    },
  },
  {
    name: "shortName",
    previewSize: {
      sm: 12,
      md: 4,
    },
    inputProps: {
      type: "text",
      label: "Nickname / short name:",
      placeholder: "Enter Nickname / short name",
    },
  },
  {
    name: "nationalNumber",
    previewSize: {
      sm: 12,
      md: 4,
    },
    inputProps: {
      type: "text",
      label: "Business number:",
      placeholder: "Enter National number",
    },
  },
  {
    name: "industry",
    previewSize: {
      sm: 12,
      md: 4,
    },
    inputProps: {
      type: "searcher",
      label: "Industry:",
      placeholder: "Select industry",
      labelKey: "title",
      dataPath: "common.industries",
      valueKey: "id",
      useLazyQuery: useGetIndustriesLazyQuery,
    },
  },
  {
    name: "businessType",
    previewSize: {
      sm: 12,
      md: 4,
    },
    inputProps: {
      type: "searcher",
      label: "Business type:",
      placeholder: "Business type",
      labelKey: "label",
      valueKey: "value",
      items: Object.entries(AuthAccountOrganizationBusinessTypeChoices)
        .filter(([key]) => isNaN(Number(key)))
        .map(([label, value]) => ({
          label,
          value,
        })),
    },
  },
  {
    name: "type",
    previewSize: {
      sm: 12,
      md: 4,
    },
    inputProps: {
      type: "searcher",
      label: "Company type:",
      placeholder: "Company type",
      labelKey: "label",
      valueKey: "value",
      items: [
        {
          value: AuthAccountOrganizationTypeChoices.BRANCH_OFFICE,
          label: "Branch Office",
        },
        {
          label: "Cooperative",
          value: AuthAccountOrganizationTypeChoices.COOPERATIVE,
        },

        {
          label: "General Partnership",
          value: AuthAccountOrganizationTypeChoices.GENERAL_PARTNERSHIP,
        },
        {
          label: "Limited Partnership",
          value: AuthAccountOrganizationTypeChoices.LIMITED_PARTNERSHIP,
        },
        {
          label: "Non-Profit Organization",
          value: AuthAccountOrganizationTypeChoices.NON_PROFIT_ORGANIZATION,
        },
        {
          label: "Private Corporation",
          value: AuthAccountOrganizationTypeChoices.PRIVATE_CORPORATION,
        },
        {
          label: "Public Corporation",
          value: AuthAccountOrganizationTypeChoices.PUBLIC_CORPORATION,
        },
        {
          label: "Sole Proprietorship",
          value: AuthAccountOrganizationTypeChoices.SOLE_PROPRIETORSHIP,
        },
        {
          label: "Subsidiary",
          value: AuthAccountOrganizationTypeChoices.SUBSIDIARY,
        },
      ],
    },
  },
  {
    name: "establishedAt",
    previewSize: {
      sm: 12,
      md: 4,
    },
    inputProps: {
      type: "date",
      label: "Date established:",
      placeholder: "Date established",
    },
  },
  {
    name: "size",
    previewSize: {
      sm: 12,
      md: 4,
    },
    inputProps: {
      type: "searcher",
      label: "Company size:",
      placeholder: "Company size",
      labelKey: "label",
      valueKey: "value",
      items: [
        {
          label: "1-10",
          value: AuthAccountOrganizationSizeChoices["_1_10"],
        },
        {
          label: "10-50",
          value: AuthAccountOrganizationSizeChoices["_10_50"],
        },
        {
          label: "50-100",
          value: AuthAccountOrganizationSizeChoices["_50_100"],
        },
        {
          label: "100-500",
          value: AuthAccountOrganizationSizeChoices["_100_500"],
        },
        {
          label: "500-1000",
          value: AuthAccountOrganizationSizeChoices["_500_1000"],
        },
        {
          label: "Over 1000",
          value: AuthAccountOrganizationSizeChoices.OVER_1000,
        },
      ],
    },
  },
  {
    name: "about",
    previewSize: {
      sm: 12,
    },
    inputProps: {
      type: "textarea",
      minRows: 10,
      label: "About Company:",
      placeholder: "Company description",
    },
  },
  {
    name: "sectionTitle",
    previewSize: 12,
    inputProps: {
      type: "title",
      label: "Contact information :",
      className: "text-base font-bold mt-6",
    },
  },
  {
    name: "email",
    previewSize: {
      sm: 12,
      md: 4,
    },
    inputProps: {
      type: "text",
      label: "Company email:",
      placeholder: "Enter your company email",
      isDisabled: true,
    },
    rules: {
      required: "This field is required",
      pattern: {
        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
        message: "Invalid email address",
      },
    },
  },
  {
    name: "website",
    previewSize: {
      sm: 12,
      md: 4,
    },
    inputProps: {
      type: "text",
      label: "Company website:",
      placeholder: "Ex : www.cpj.ai",
    },
    rules: {
      validate: (value) => {
        if (!value || value === "") return true;
        const formattedUrl = formatUrl(value);
        if (isValidHttpUrl(formattedUrl)) return true;
        else {
          return "Enter valid website";
        }
      },
    },
  },
  {
    name: "city",
    previewSize: {
      sm: 12,
      md: 4,
    },
    inputProps: {
      type: "searcher",
      label: "Location (city):",
      placeholder: "Location (city)",
      useLazyQuery: useGetCitiesLazyQuery,
      dataPath: "common.cities",
      refetchDataKey: typeof formData?.city === "string" ? formData?.city : formData?.city?.node?.id,
    },
  },
  {
    name: "contactNumber",
    previewSize: {
      sm: 12,
      md: 4,
    },
    inputProps: {
      type: "phone",
      label: "Contact Number:",
      placeholder: "Enter Contact Number",
    },
    rules: {
      validate: (value) => {
        if (!value) return true;
        return isValidPhoneNumber(value) || "Please enter a valid phone number";
      },
    },
  },
  {
    name: "address",
    previewSize: {
      sm: 12,
      md: 8,
    },
    inputProps: {
      type: "text",
      label: "Address:",
      placeholder: "Enter address",
    },
  },
];
